import { Component, OnInit, Input } from '@angular/core';
import { NgFor, NgIf, DatePipe, NgClass } from '@angular/common'; // Added DatePipe for potential formatting
import { FormsModule } from '@angular/forms';
import { HttpParams } from '@angular/common/http';
import { InvoiceService } from '../../../../Services/Invoice/invoice.service';
import { NotificationService } from '../../../../Services/notification.service';
import { Router, RouterLink } from '@angular/router';
import { InvoiceserviceService } from '../../../../Services/invoiceservice.service';

// Import for export functionality
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import FileSaver from 'file-saver';
import Swal from 'sweetalert2';


@Component({
  selector: 'app-invoice-list',
  standalone: true,
  imports: [FormsModule, NgFor, NgIf, RouterLink,NgClass], // Added DatePipe
  templateUrl: './invoice-list.component.html',
  styleUrls: ['./invoice-list.component.css'],
})
export class InvoiceListComponent implements OnInit {
  @Input() kalakarId!: number;

  invoiceList: any[] = [];
  paginatedInvoiceList: any[] = [];

  currentPage = 1;
  itemsPerPage = 10;
  totalPages = 0;

  globalSearchText = '';
  searchValues: { [key: string]: string } = {};

  sortKey: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  showDropdown: boolean = false; // For column selection dropdown

selectedFields: { key: string; label: string; selected: boolean; subKey?: string }[] = [
  // --- Basic Info ---
  { key: 'invoice_number', label: 'Invoice No', selected: true },
  { key: 'customer_name', label: 'Customer Name', selected: true },
  { key: 'invoice_date', label: 'Date', selected: true },
  { key: 'salesperson_name', label: 'Salesperson', selected: false },
  { key: 'phone', label: 'Phone', selected: false },

  // --- Amounts & Totals ---
  { key: 'total_amount_before_disc', label: 'Sub Total (Before Disc)', selected: true },
  { key: 'total_discount', label: 'Total Discount', selected: true },
  { key: 'grand_total', label: 'Grand Total', selected: true },
  { key: 'metal_received', label: 'Metal Received', selected: false },
  { key: 'hallmark_charges', label: 'Hallmark Charges', selected: false },
  { key: 'courier_charges', label: 'Courier Charges', selected: false },
  { key: 'round_off', label: 'Round Off', selected: false },


  // --- Tax Details ---
  { key: 'taxable_amount', label: 'Taxable Amount', selected: false },
  { key: 'cgst', label: 'CGST Amount', selected: false },
  { key: 'sgst', label: 'SGST Amount', selected: false },
  { key: 'igst', label: 'IGST Amount', selected: false },

  // --- Payment Status ---
  { key: 'amount_paid', label: 'Amount Paid', selected: true },
  { key: 'balance_due', label: 'Balance Due', selected: true },
  { key: 'status', label: 'Status', selected: true },

  // --- Payment Methods (Detailed) ---
  { key: 'payment_mode', label: 'Payment Mode', selected: false },
  { key: 'cash_amount', label: 'Cash Amount', selected: false },
  { key: 'bank_amount', label: 'Bank/Cheque Amount', selected: false },
  { key: 'card_amount', label: 'Card Amount', selected: false },
  { key: 'online_amount', label: 'Online Amount', selected: false },

  // --- Audit/Other Details ---
  { key: 'created_at', label: 'Created At', selected: false },
  { key: 'notes', label: 'Notes', selected: false },
];


  constructor(
    private invoiceService: InvoiceserviceService,
    private notificationService: NotificationService,
    public router: Router
  ) {}

  ngOnInit(): void {
    const savedSelections = localStorage.getItem('invoiceColumnSelections');
    if (savedSelections) {
      this.selectedFields = JSON.parse(savedSelections);
    }
    this.getInvoiceList();
  }

  getInvoiceList() {
    this.invoiceService.getInvoices().subscribe(
      (res: any) => {
        this.invoiceList = Array.isArray(res) ? res : (res.data || []);

        this.selectedFields.forEach(field => {
          this.searchValues[field.key] = this.searchValues[field.key] || '';
        });

        this.filterTable();
      },
      () => {
        this.notificationService.showError('Failed to fetch invoices', 'Error');
      }
    );
  }


  filterTable() {
    let filtered = this.invoiceList;

    // Global filter
    if (this.globalSearchText.trim()) {
      filtered = filtered.filter((row: any) =>
        Object.values(row).some((val) =>
          String(val).toLowerCase().includes(this.globalSearchText.toLowerCase())
        )
      );
    }

    // Individual column filter
    Object.keys(this.searchValues).forEach(key => {
      if (this.searchValues[key]?.trim()) {
        filtered = filtered.filter(item =>
          String(item[key]).toLowerCase().includes(this.searchValues[key].toLowerCase())
        );
      }
    });

    // Sorting
    if (this.sortKey) {
      filtered.sort((a, b) => {
        const valA = a[this.sortKey] ?? '';
        const valB = b[this.sortKey] ?? '';

        // Simple comparison for strings/numbers
        return (this.sortDirection === 'asc'
          ? valA > valB ? 1 : valA < valB ? -1 : 0
          : valA < valB ? 1 : valA > valB ? -1 : 0);
      });
    }

    // Pagination
    this.totalPages = Math.ceil(filtered.length / this.itemsPerPage);
    // Ensure currentPage is not greater than totalPages after filtering
    if (this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages > 0 ? this.totalPages : 1;
    }
    const start = (this.currentPage - 1) * this.itemsPerPage;
    this.paginatedInvoiceList = filtered.slice(start, start + this.itemsPerPage);
  }

  // --- Utility/Helper Methods ---

  get visibleFields() {
    return this.selectedFields.filter((field) => field.selected);
  }

  formatNumber(value: any): string {
    const num = parseFloat(value);
    return isNaN(num) ? '-' : num.toFixed(2);
  }

  formatFieldValue(item: any, field: any): string {
    const value = field.subKey ? item[field.key]?.[field.subKey] : item[field.key];

    if (value == null || value === '') return '-';

    // Format numbers
    if (!isNaN(value) && typeof value !== 'object') {
      return this.formatNumber(value);
    }

    return value;
  }

  // --- Pagination Methods ---

  onPageChange(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.filterTable();
    }
  }

  goToPreviousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.filterTable();
    }
  }

  goToNextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.filterTable();
    }
  }

  get pageNumbers(): number[] {
    return Array(this.totalPages)
      .fill(0)
      .map((_, i) => i + 1);
  }

  // --- Sorting Method ---

  sortBy(key: string) {
    if (this.sortKey === key) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = key;
      this.sortDirection = 'asc';
    }
    this.filterTable();
  }

  // --- Column Visibility Methods ---

  toggleDropdown() {
    this.showDropdown = !this.showDropdown;
  }

  applyColumnSelection() {
    localStorage.setItem('invoiceColumnSelections', JSON.stringify(this.selectedFields));
    this.showDropdown = false;
    this.filterTable(); // Re-render table with new columns
  }

  selectAllFields() {
    this.selectedFields.forEach((field) => (field.selected = true));
    this.applyColumnSelection();
  }

  deselectAllFields() {
    this.selectedFields.forEach((field) => (field.selected = false));
    this.applyColumnSelection();
  }

  restoreColumnSelection() {
    localStorage.removeItem('invoiceColumnSelections');
    this.selectedFields = [

  // --- Basic Info ---
  { key: 'invoice_number', label: 'Invoice No', selected: true },
  { key: 'customer_name', label: 'Customer Name', selected: true },
  { key: 'invoice_date', label: 'Date', selected: true },
  { key: 'salesperson_name', label: 'Salesperson', selected: false },
  { key: 'phone', label: 'Phone', selected: false },

  // --- Amounts & Totals ---
  { key: 'total_amount_before_disc', label: 'Sub Total (Before Disc)', selected: true },
  { key: 'total_discount', label: 'Total Discount', selected: true },
  { key: 'grand_total', label: 'Grand Total', selected: true },
  { key: 'metal_received', label: 'Metal Received', selected: false },
  { key: 'hallmark_charges', label: 'Hallmark Charges', selected: false },
  { key: 'courier_charges', label: 'Courier Charges', selected: false },
  { key: 'round_off', label: 'Round Off', selected: false },


  // --- Tax Details ---
  { key: 'taxable_amount', label: 'Taxable Amount', selected: false },
  { key: 'cgst', label: 'CGST Amount', selected: false },
  { key: 'sgst', label: 'SGST Amount', selected: false },
  { key: 'igst', label: 'IGST Amount', selected: false },

  // --- Payment Status ---
  { key: 'amount_paid', label: 'Amount Paid', selected: true },
  { key: 'balance_due', label: 'Balance Due', selected: true },
  { key: 'status', label: 'Status', selected: true },

  // --- Payment Methods (Detailed) ---
  { key: 'payment_mode', label: 'Payment Mode', selected: false },
  { key: 'cash_amount', label: 'Cash Amount', selected: false },
  { key: 'bank_amount', label: 'Bank/Cheque Amount', selected: false },
  { key: 'card_amount', label: 'Card Amount', selected: false },
  { key: 'online_amount', label: 'Online Amount', selected: false },

  // --- Audit/Other Details ---
  { key: 'created_at', label: 'Created At', selected: false },
  { key: 'notes', label: 'Notes', selected: false },
];
    this.applyColumnSelection();
  }

  // --- Details Modal Method (Using SweetAlert) ---

  openInvoiceDetails(id: number): void {
    // NOTE: You need a service method to get a single invoice by ID. Assuming 'getInvoiceById' exists.
    this.invoiceService.getInvoiceById(id).subscribe(
      (response: any) => {
        const invoice = response.data || response; // Adjust based on your API response structure

        // Dynamically generate rows based on selected fields
        const rows = this.selectedFields
          .map(field => {
            let value = invoice[field.key];

            // Use the formatFieldValue logic for consistency
            const formattedValue = this.formatFieldValue(invoice, field);

            return `<p><strong>${field.label}:</strong> ${formattedValue}</p>`;
          })
          .join('');

        Swal.fire({
          title: `<strong style="font-size: 20px; color: #17a2b8;">Invoice Details</strong>`,
          html: `
            <div style="text-align: left; font-size: 14px; max-height: 70vh; overflow-y: auto; font-family: 'Segoe UI', sans-serif;">
              <section style="margin-bottom: 1.5rem;">
                <h3 style="background: #e9f7fe; color: #17a2b8; padding: 6px 10px; border-left: 4px solid #17a2b8;">General Info</h3>
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; padding-top: 8px;">
                  ${rows}
                </div>
              </section>
              <p style="margin-top: 20px; text-align: center; color: #6c757d;">(Displaying all configured fields)</p>
            </div>
          `,
          showCloseButton: true,
          focusConfirm: false,
          confirmButtonText: '<span style="color: white;">Close</span>',
          width: '650px',
          scrollbarPadding: false,
          customClass: {
            // You might need to define these classes in your CSS file
            popup: 'invoice-details-popup',
            confirmButton: 'invoice-confirm-button'
          }
        });
      },
      (error) => {
        console.error('Error fetching invoice details', error);
        Swal.fire('Error', 'Unable to fetch invoice details. Please try again.', 'error');
      }
    );
  }

  // --- Export Methods ---

  downloadPDF(): void {
    const doc = new jsPDF('l', 'mm', 'a4');
    const selectedFields = this.visibleFields;

    const headers = [
      { header: 'No.', dataKey: 'no' },
      ...selectedFields.map((field) => ({
        header: field.label,
        dataKey: field.key,
      })),
    ];

    const data = this.paginatedInvoiceList.map((item, index) => {
      const row: any = { no: (this.currentPage - 1) * this.itemsPerPage + index + 1 };
      selectedFields.forEach((field) => {
        row[field.key] = this.formatFieldValue(item, field);
      });
      return row;
    });

    autoTable(doc, {
      columns: headers,
      body: data,
      styles: { fontSize: 6, cellPadding: 1.5, overflow: 'linebreak' },
      headStyles: {
        fillColor: [23, 162, 184], // Bootstrap info blue
        textColor: 255,
        halign: 'center',
        fontSize: 7,
      },
      startY: 10,
      margin: { top: 10, left: 5, right: 5 },
      theme: 'grid',
      tableWidth: 'wrap',
      didDrawPage: (dataArg) => {
        const pageSize = doc.internal.pageSize;
        const pageHeight = pageSize.height;
        doc.setFontSize(8);
        doc.text(
          `Page ${(doc as any).getNumberOfPages()}`,
          pageSize.width - 20,
          pageHeight - 10
        );
      },
    });

    doc.save('invoice-list.pdf');
  }

  downloadExcel(): void {
    const selectedFields = this.visibleFields;

    const data = this.paginatedInvoiceList.map((item, index) => {
      const row: any = { 'No.': (this.currentPage - 1) * this.itemsPerPage + index + 1 };
      selectedFields.forEach(field => {
        row[field.label] = this.formatFieldValue(item, field);
      });
      return row;
    });

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);
    const workbook: XLSX.WorkBook = {
      Sheets: { 'Invoice List': worksheet },
      SheetNames: ['Invoice List']
    };

    const excelBuffer: any = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array'
    });

    const fileName = 'invoice-list.xlsx';
    const dataBlob: Blob = new Blob([excelBuffer], {
      type: 'application/octet-stream'
    });
    FileSaver.saveAs(dataBlob, fileName);
  }

  downloadJSONAll(): void {
    const data = this.paginatedInvoiceList || [];
    const jsonString = JSON.stringify(data, null, 2);

    const blob = new Blob([jsonString], { type: 'application/json' });
    const fileName = 'invoice-list.json';

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();

    URL.revokeObjectURL(link.href);
  }

  downloadCSV(): void {
    const data = this.paginatedInvoiceList || [];

    if (!data.length) return;

    const selectedFields = this.visibleFields;
    const headers = ['No.', ...selectedFields.map(f => f.label)];
    const keys = selectedFields.map(f => f.key);

    let csvContent = headers.join(',') + '\n';

    data.forEach((item, index) => {
      const row = [
        (this.currentPage - 1) * this.itemsPerPage + index + 1,
        ...keys.map(key => {
          const field = selectedFields.find(f => f.key === key);
          const value = this.formatFieldValue(item, field);

          return typeof value === 'string' && value.includes(',')
            ? `"${value}"`
            : value ?? '';
        }),
      ];
      csvContent += row.join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const fileName = 'invoice-list.csv';

    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', fileName);
    link.click();

    URL.revokeObjectURL(link.href);
  }

  // --- Print Method ---

  deleteInvoice(id: number, invoiceNumber?: string): void {
    const label = invoiceNumber ? `invoice ${invoiceNumber}` : 'this invoice';

    Swal.fire({
      title: `Delete ${label}?`,
      text: 'Put sold items back into stock?',
      icon: 'warning',
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: 'Yes',
      denyButtonText: 'No',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#198754',
      denyButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
    }).then((result) => {
      if (result.isDismissed) {
        return;
      }

      const restoreStock = result.isConfirmed;
      this.invoiceService.deleteInvoice(id, restoreStock).subscribe({
        next: () => {
          this.invoiceList = this.invoiceList.filter((inv) => inv.id !== id);
          this.filterTable();
          this.notificationService.showSuccess('Invoice deleted successfully', 'Success');
        },
        error: (err) => {
          const message = err?.error?.message || 'Failed to delete invoice. Please try again.';
          this.notificationService.showError(message, 'Error');
        }
      });
    });
  }

  printTable() {
    let printContent = `<html><head>
      <title>Invoice List</title>
      <style>
        table { border-collapse: collapse; width: 100%; font-size: 12px; }
        th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
        th { background-color: #f2f2f2; }
      </style>
    </head><body>
      <h3>Invoice List (Page ${this.currentPage} of ${this.totalPages})</h3>
      <table>
        <thead>
          <tr>
            <th>No.</th>`;

    // Add visible column headers
    this.visibleFields.forEach(field => {
        printContent += `<th>${field.label}</th>`;
    });

    printContent += `</tr></thead><tbody>`;


    // Add data rows
    this.paginatedInvoiceList.forEach((item, index) => {
      printContent += `<tr>
        <td>${(this.currentPage - 1) * this.itemsPerPage + index + 1}</td>`;

      this.visibleFields.forEach(field => {
        printContent += `<td>${this.formatFieldValue(item, field)}</td>`;
      });

      printContent += `</tr>`;
    });

    printContent += `</tbody></table></body></html>`;

    // Open new window for print
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.print();
    }
  }
}
